// @ts-check
/* global mixpanel */
import {Component} from 'panel';
import omit from 'lodash/omit';

import {SPECIAL_SERIES_NAMES} from 'common/report/constants';
import {renameProperty, renamePropertyValue} from 'common/report/util';
import {registerMPElement} from 'common/util/register-element';

import {CUSTOM_ALERT_FUNNELS_DEFAULT_KEYS} from '../custom-alert/constants';
import {smartHubApi} from '../dropdown/api';
import {Errors, ErrorToErrorText, ErrorToEventName} from './constants';

import template from './index.jade';
import css from './index.styl?inline';

export default registerMPElement(
  `mp-report-config`,
  class SmartHubReport extends Component {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'bookmark-id': `number`,
        'display-context': `string`,
        'enable-custom-alert-condition-types': {type: `boolean`, default: false},
        'enable-custom-alerts': {type: `boolean`, default: false},
        'enable-subscribe-alerts': {type: `boolean`, default: false},
        'has-empty-data': {type: `boolean`, default: false},
        'is-report-loading': {type: `boolean`, default: false},
        'project-id': `string`,
        'results-keys-and-values': {type: `json`, default: null},
        'time-unit': `string`,
        'unsaved-changes': {type: `boolean`, default: false},
        'utc-offset': `string`,
        'workspace-id': `number`,
      };
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,
        defaultState: {
          bookmarkAlertSubscriptions: {},
          bookmarkId: null,
          customAlertModalOpen: false,
          hasUnreadAlerts: false,
          open: false,
        },
        helpers: {
          handleClick: () => {
            if (this.state.bookmarkId) {
              mixpanel.track(`[Alerts] Open Alert Menu`, this.trackingProperties());
              this.update({open: !this.state.open});
            }
          },
          getIcon: () => {
            let icon = `alert-active`;
            if (this.attr(`unsaved-changes`) || this.noSubscriptionOrPaused()) {
              icon = `alert-normal`;
            } else if (this.state.hasUnreadAlerts) {
              icon = `alert-tilted`;
            }
            return icon;
          },
          getTooltipText: () => {
            let text = `View alerts`;
            if (!this.state.bookmarkId) {
              text = `To enable alerts, save your report`;
            } else if (this.attr(`unsaved-changes`)) {
              text = `You've edited this report.  Save your changes to enable alerts.`;
            } else if (this.noSubscriptionOrPaused()) {
              text = `Enable alerts`;
            }
            return text;
          },
          handleMenuChange: ({detail = {}} = {}) => {
            if (this.state.open && detail.state === `closed`) {
              this.update({open: false});
            }
          },
          clickedAutomatedAlertToggle: () => {
            if (this.hasAutomatedAlertSubscription()) {
              this.updateAutomatedAlertSubscription();
            } else {
              this.createAutomatedAlertSubscription();
            }
          },
          isAutomatedAlertSubscriptionPaused: () => {
            const {bookmarkAlertSubscriptions, bookmarkId} = this.state;
            const alert = bookmarkAlertSubscriptions[bookmarkId];
            return alert ? alert.paused : true;
          },
          markAlertsLoaded: (ev) => {
            const {alerts} = ev.detail;
            const bookmarkedAlerts = alerts.filter((a) => a.reportId === this.attr(`bookmark-id`));
            this.update({
              hasUnreadAlerts: !!bookmarkedAlerts.find((a) => !a.readAtMs),
            });
          },
          handleAlertsMarkedAsRead: () => this.update({hasUnreadAlerts: false}),
          showUnreadStatus: () =>
            this.state.hasUnreadAlerts &&
            this.hasAutomatedAlertSubscription() &&
            !this.helpers.isAutomatedAlertSubscriptionPaused(),
          clickedCreateCustomAlert: () => this.update({open: false, customAlertModalOpen: true}),
          closeCustomAlertModal: () => this.update({customAlertModalOpen: false}),
          handleCustomAlertCreated: () => {
            this.addToast({
              cta: `Manage Alerts`,
              ctaCallback: () => window.open(`/settings/account#alert-settings`, `_blank`),
              message: `Custom alert created.`,
            });
            this.helpers.closeCustomAlertModal();
          },
          handleCustomAlertError: () => {
            this.addToast({message: `Failed to create custom alert.`, 'is-error': true});
            this.helpers.closeCustomAlertModal();
          },
          handleManageAlerts: (ev) => {
            ev.stopPropagation();
            if (!ev.detail.modifierKey) {
              this.update({open: false});
            }
          },
          handleClickedLink: (ev) => {
            ev.stopPropagation();
            if (!ev.detail.modifierKey) {
              this.update({open: false});
            }
          },
          getReportTypeFromContext: () => {
            switch (this.attr(`display-context`)) {
              case `FUNNELS`:
                return `funnels`;
              case `INSIGHTS`:
                return `insights`;
              default:
                return ``;
            }
          },
          isPercentageThreshold: (resultsKeysAndValues) => {
            if (resultsKeysAndValues && resultsKeysAndValues.length > 0) {
              return resultsKeysAndValues[resultsKeysAndValues.length - 1].isPercentage === true;
            } else {
              return false;
            }
          },
          getResultsKeysAndValues: () => {
            switch (this.attr(`display-context`)) {
              case `FUNNELS`:
                return CUSTOM_ALERT_FUNNELS_DEFAULT_KEYS;
              case `INSIGHTS`:
                if (this.attr(`results-keys-and-values`)) {
                  return this.attr(`results-keys-and-values`).map((key) => ({
                    ...key,
                    displayName: key.displayName || SPECIAL_SERIES_NAMES[key.name] || renameProperty(key.name),
                    displayValues: key.displayValues || key.values.map((value) => renamePropertyValue(value, key.name)),
                  }));
                } else {
                  return null;
                }
            }
          },
        },
      };
    }

    connectedCallback() {
      this.getAlertSubscriptions();
      super.connectedCallback();
    }

    attributeChangedCallback(attr, oldValue, newValue) {
      super.attributeChangedCallback(attr, oldValue, newValue);
      switch (attr) {
        case `bookmark-id`:
          this.update({bookmarkId: this.getJSONAttribute(attr)});
          break;
      }
    }

    hasAutomatedAlertSubscription() {
      const {bookmarkAlertSubscriptions, bookmarkId} = this.state;
      return bookmarkId && bookmarkAlertSubscriptions[bookmarkId];
    }

    noSubscriptionOrPaused() {
      return !this.hasAutomatedAlertSubscription() || this.helpers.isAutomatedAlertSubscriptionPaused();
    }

    trackingProperties() {
      return {
        'bookmark-id': this.attr(`bookmark-id`),
        report: this.attr(`display-context`),
      };
    }

    handleError(results, errorType) {
      mixpanel.track(ErrorToEventName[errorType], {
        ...this.trackingProperties(),
        'status code': results.status,
        error: results.error,
      });
      const message = ErrorToErrorText[results.status] || ErrorToErrorText[errorType];
      this.addToast({message, 'is-error': true});
    }

    addToast() {
      const toastArea = document.querySelector(`mp-toast-area`);
      if (toastArea) {
        // @ts-ignore
        toastArea.addToast(...arguments);
      }
    }

    mergeUpdateAlertSubscription(bookmarkId, update = {}) {
      const {bookmarkAlertSubscriptions} = this.state;
      const alert = {
        ...(bookmarkAlertSubscriptions[bookmarkId] || {}),
        ...update,
      };
      this.update({
        bookmarkAlertSubscriptions: {
          ...bookmarkAlertSubscriptions,
          [bookmarkId]: alert,
        },
      });
    }

    async createAutomatedAlertSubscription() {
      const {bookmarkId} = this.state;
      this.mergeUpdateAlertSubscription(bookmarkId, {paused: false});
      const results = await smartHubApi({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      }).createAlertSubscription(bookmarkId);
      if (results.error) {
        this.update({
          bookmarkAlertSubscriptions: omit(this.state.bookmarkAlertSubscriptions, bookmarkId),
        });
        this.handleError(results, Errors.Create);
      } else {
        this.mergeUpdateAlertSubscription(bookmarkId, results);
        this.update({open: true});
        mixpanel.track(`[Alerts] Create Alert`, this.trackingProperties());
      }
    }

    async updateAutomatedAlertSubscription() {
      const {bookmarkId} = this.state;
      const originalAlert = {...this.state.bookmarkAlertSubscriptions[bookmarkId]};
      this.mergeUpdateAlertSubscription(bookmarkId, {paused: !originalAlert.paused});
      const results = await smartHubApi({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      }).updateAlertSubscription(originalAlert.id, {
        paused: !originalAlert.paused,
      });
      if (results.error) {
        this.handleError(results, Errors.Update);
        this.mergeUpdateAlertSubscription(bookmarkId, originalAlert);
      } else {
        mixpanel.track(`[Alerts] Update Alert`, {
          ...this.trackingProperties(),
          paused: !originalAlert.paused,
        });
      }
    }

    async getAlertSubscriptions() {
      const results = await smartHubApi({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      }).getAlertSubscriptions();
      if (results.error) {
        this.handleError(results, Errors.Retrieve);
      } else {
        const bookmarkAlertSubscriptions = Object.assign({}, ...results.map((alert) => ({[alert.bookmark.id]: alert})));
        this.update({bookmarkAlertSubscriptions});
      }
    }
  },
);
